import React from "react";

/**
 * Root error boundary.
 *
 * Without this, any module-level or render-time throw unmounts the whole tree
 * and the user sees a bare white page with no clue what happened (exactly the
 * failure mode we hit in production). This renders the actual error instead.
 */
type Props = { children: React.ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the full detail in the console for debugging deployed builds.
    console.error("Unhandled application error:", error, info.componentStack);
  }

  private handleReload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-lg w-full rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            The app hit an unexpected error while loading. Reloading usually
            helps. If it keeps happening, share the details below with the team.
          </p>

          <pre className="mt-4 max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-200">
            {error.message}
          </pre>

          <button
            type="button"
            onClick={this.handleReload}
            className="mt-4 inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
